import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useWalletConnect } from "@btc-vision/walletconnect";
import {
  useAllEscrows,
  useEscrowCount,
  useTokenMetadata,
  formatTokenAmount,
  getStateName,
  getStateColor,
  type EscrowData,
} from "../hooks/useEscrow";

type FilterMode = "all" | "my-buyer" | "my-seller" | "my-arbitrator";

// Must match EscrowCore STATE_* constants (3=Released, 4=Disputed, 5=Resolved, 7=Expired)
const STATE_FILTERS = [
  { label: "Todos", value: -1 },
  { label: "Created", value: 0 },
  { label: "Funded", value: 1 },
  { label: "Active", value: 2 },
  { label: "Released", value: 3 },
  { label: "Disputed", value: 4 },
  { label: "Resolved", value: 5 },
  { label: "Expired", value: 7 },
];

export function EscrowListPage() {
  const { walletAddress } = useWalletConnect();
  const { data: count, isLoading: countLoading } = useEscrowCount();
  const { data: escrows, isLoading: escrowsLoading } = useAllEscrows();

  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [stateFilter, setStateFilter] = useState(-1);
  const [searchId, setSearchId] = useState("");

  const isLoading = countLoading || escrowsLoading;

  // Filter escrows
  const filtered = (escrows ?? []).filter((e) => {
    // Skip empty/non-existent escrows (contract returns zeros for invalid IDs)
    if (/^0x0+$/.test(e.buyer.toLowerCase()) && e.amount === 0n && e.createdAt === 0n) {
      return false;
    }

    // State filter
    if (stateFilter >= 0 && e.state !== stateFilter) return false;

    // Search by ID
    if (searchId.trim()) {
      const searchNum = parseInt(searchId.trim(), 10);
      if (!isNaN(searchNum) && Number(e.id) !== searchNum) return false;
    }

    // Role filter
    if (filterMode !== "all" && walletAddress) {
      const addr = walletAddress.toLowerCase();
      switch (filterMode) {
        case "my-buyer":
          if (!e.buyer.toLowerCase().includes(addr.slice(-8))) return false;
          break;
        case "my-seller":
          if (!e.seller.toLowerCase().includes(addr.slice(-8))) return false;
          break;
        case "my-arbitrator":
          if (!e.arbitrator.toLowerCase().includes(addr.slice(-8))) return false;
          break;
      }
    }

    return true;
  });

  // Show newest first
  const sorted = [...filtered].reverse();

  return (
    <div className="page">
      <div className="page-header">
        <h1>Escrows</h1>
        <p style={{ color: "var(--text-secondary)" }}>
          {count !== undefined
            ? `${count.toString()} escrow${count !== 1n ? "s" : ""} on-chain`
            : "Conecta tu wallet para ver escrows"}
        </p>
      </div>

      {/* Filters bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 24,
          alignItems: "center",
        }}
      >
        {/* Search by ID */}
        <input
          type="number"
          placeholder="Buscar por ID..."
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          style={{
            padding: "8px 14px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text-primary)",
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            width: 140,
          }}
        />

        {/* Role filter */}
        <div style={{ display: "flex", gap: 4 }}>
          {(
            [
              { label: "Todos", value: "all" },
              { label: "Buyer", value: "my-buyer" },
              { label: "Seller", value: "my-seller" },
              { label: "Arbitro", value: "my-arbitrator" },
            ] as { label: string; value: FilterMode }[]
          ).map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterMode(f.value)}
              disabled={f.value !== "all" && !walletAddress}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                borderRadius: "var(--radius-sm)",
                border:
                  filterMode === f.value
                    ? "1px solid var(--purple)"
                    : "1px solid var(--border)",
                background:
                  filterMode === f.value
                    ? "rgba(111,76,255,0.15)"
                    : "rgba(255,255,255,0.03)",
                color:
                  filterMode === f.value
                    ? "var(--purple-light)"
                    : "var(--text-secondary)",
                cursor: "pointer",
                fontFamily: "var(--font)",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* State filter */}
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(Number(e.target.value))}
          style={{
            padding: "8px 12px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text-primary)",
            fontSize: 13,
            fontFamily: "var(--font)",
            cursor: "pointer",
          }}
        >
          {STATE_FILTERS.map((sf) => (
            <option key={sf.value} value={sf.value}>
              {sf.label}
            </option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="card"
              style={{ padding: 20, opacity: 0.5 }}
            >
              <div
                className="skeleton"
                style={{ height: 18, width: 200, marginBottom: 8 }}
              />
              <div
                className="skeleton"
                style={{ height: 14, width: 350 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* No escrows */}
      {!isLoading && sorted.length === 0 && (
        <div
          className="card"
          style={{
            padding: 40,
            textAlign: "center",
            color: "var(--text-muted)",
          }}
        >
          {count === 0n || count === undefined
            ? "No hay escrows creados aun."
            : "No se encontraron escrows con esos filtros."}
          <br />
          <Link
            to="/create"
            className="btn btn-primary"
            style={{ marginTop: 16, display: "inline-block" }}
          >
            Crear Escrow
          </Link>
        </div>
      )}

      {/* Escrow list */}
      {!isLoading && sorted.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {sorted.map((escrow) => (
            <EscrowCard key={escrow.id.toString()} escrow={escrow} />
          ))}
        </div>
      )}
    </div>
  );
}

function EscrowCard({ escrow }: { escrow: EscrowData }) {
  const stateColor = getStateColor(escrow.state);
  const stateName = getStateName(escrow.state);
  const { data: tokenMeta } = useTokenMetadata(escrow.token);
  const decimals = tokenMeta?.decimals ?? 8;
  const symbol = tokenMeta?.symbol ?? "tokens";
  const amount = formatTokenAmount(escrow.amount, decimals);

  return (
    <Link
      to={`/escrow/${escrow.id.toString()}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div
        className="card"
        style={{
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          cursor: "pointer",
          transition: "all 0.2s",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          background: "var(--bg-card)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--border-hover)";
          e.currentTarget.style.background = "var(--bg-card-hover)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.background = "var(--bg-card)";
        }}
      >
        {/* ID */}
        <div
          style={{
            minWidth: 60,
            fontFamily: "var(--font-mono)",
            fontSize: 16,
            fontWeight: 700,
            color: "var(--purple-light)",
          }}
        >
          #{escrow.id.toString()}
        </div>

        {/* State badge */}
        <div
          style={{
            padding: "4px 10px",
            borderRadius: "var(--radius-pill)",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: stateColor,
            background: `${stateColor}22`,
            border: `1px solid ${stateColor}44`,
            whiteSpace: "nowrap",
          }}
        >
          {stateName}
        </div>

        {/* Amount */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {amount} {symbol}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            Buyer: {shortenAddr(escrow.buyer)}
            {" | "}
            Seller: {shortenAddr(escrow.seller)}
          </div>
        </div>

        {/* Deadlines */}
        <div
          style={{
            textAlign: "right",
            fontSize: 11,
            color: "var(--text-muted)",
            whiteSpace: "nowrap",
          }}
        >
          <div>Fund DL: bloque {escrow.fundDeadline.toString()}</div>
          <div>Trade DL: bloque {escrow.tradeDeadline.toString()}</div>
        </div>

        {/* Arrow */}
        <div style={{ color: "var(--text-muted)", fontSize: 18 }}>
          &rsaquo;
        </div>
      </div>
    </Link>
  );
}

function shortenAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || "---";
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

function formatBigInt(val: bigint): string {
  // Simple formatter: divide by 10^8 if large, otherwise show raw
  if (val >= 100_000_000n) {
    const whole = val / 100_000_000n;
    const frac = val % 100_000_000n;
    const fracStr = frac.toString().padStart(8, "0").replace(/0+$/, "");
    return fracStr ? `${whole}.${fracStr}` : whole.toString();
  }
  return val.toString();
}
